import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart, cartSessionId } from "@/context/CartContext";
import { isAgeVerified } from "@/components/AgeGate";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { jmd } from "@/lib/format";
import { COUNTRIES } from "@/lib/countries";
import { MapPin, Truck, CreditCard, Wallet, Check, Loader2, Store, Banknote, Globe, Landmark, Copy, Upload } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { HaulagePinMap } from "@/components/haulage/HaulagePinMap";
import { PaymentLogos, PAYMENT_METHODS_TEXT } from "@/components/PaymentLogos";
import { WeatherNotice } from "@/components/WeatherNotice";

const STEPS = ["Address", "Delivery", "Payment", "Review"];

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [parishes, setParishes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState({
    full_name: "", phone: "", line1: "", parish: "Kingston", city: "", country: "Jamaica", notes: "", email: "", lat: null, lng: null, delivery_window: null,
  });
  const [delivery, setDelivery] = useState("standard");
  const [deliveryPartner, setDeliveryPartner] = useState("SHOP NOW Express");
  const [payment, setPaymentState] = useState(() => localStorage.getItem("sn_last_payment") || "card");
  const setPayment = (v) => { setPaymentState(v); localStorage.setItem("sn_last_payment", v); };
  const [fygaroOn, setFygaroOn] = useState(false);
  const [paypalOn, setPaypalOn] = useState(false);
  const [paypalClientId, setPaypalClientId] = useState("");
  const [usePoints, setUsePoints] = useState(0);
  const [bankInfo, setBankInfo] = useState(null);
  const [proofUrl, setProofUrl] = useState(null);
  const [uploadingProof, setUploadingProof] = useState(false);

  useEffect(() => {
    api.get("/meta").then(({ data }) => setParishes(data.parishes)).catch(() => {});
    api.get("/payments/fygaro/status").then(({ data }) => { setFygaroOn(!!data.enabled); if (!data.enabled && localStorage.getItem("sn_last_payment") === "fygaro") setPaymentState("card"); }).catch(() => {});
    api.get("/paypal/status").then(({ data }) => { setPaypalOn(!!data.enabled); setPaypalClientId(data.client_id || ""); if (!data.enabled && localStorage.getItem("sn_last_payment") === "paypal") setPaymentState("card"); }).catch(() => {});
    if (user) { setForm((f) => ({ ...f, full_name: user.name, email: user.email })); api.get("/my/summary").then(({ data }) => setSummary(data)).catch(() => {}); }
  }, [user]);

  useEffect(() => { if (items.length === 0 && !loading) navigate("/cart"); }, [items.length]);

  const intl = form.country && form.country !== "Jamaica";
  const quoted = delivery === "marketplace" || delivery === "haulage";
  const fee = delivery === "pickup" || quoted ? 0 : intl ? 6500 : delivery === "express" ? 1500 : (summary?.shop_plus ? 0 : 800);
  const discountPct = summary?.discount_pct || 0;
  const discountAmt = Math.round(subtotal * discountPct / 100);
  const discounted = subtotal - discountAmt;
  const tax = Math.round(discounted * 15 / 115);
  const pointsVal = Math.min(usePoints, summary?.points || 0, 10000);
  const total = Math.max(0, discounted + fee - pointsVal);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const buildPayload = () => ({
    items: items.map((i) => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity, image: i.image, merchant_slug: i.merchant_slug, branch_id: i.branch_id, note: i.note, options: i.options })),
    address: { full_name: form.full_name, phone: form.phone, line1: form.line1, parish: form.parish, city: form.city, country: form.country, notes: form.notes, lat: form.lat, lng: form.lng, delivery_window: form.delivery_window || null },
    delivery_method: delivery, delivery_partner: deliveryPartner, payment_method: payment, age_confirmed: isAgeVerified(),
    email: user ? user.email : form.email, origin_url: window.location.origin, use_points: pointsVal,
    cart_session_id: cartSessionId(),
  });

  const paypalCreate = async () => {
    const { data } = await api.post("/checkout", { ...buildPayload(), payment_method: "paypal" });
    window.__ppOrderNo = data.order_no;
    return data.paypal_order_id;
  };
  const paypalApprove = async (data) => {
    try {
      await api.post(`/paypal/capture/${data.orderID}`);
      clear();
      toast.success("Payment complete!");
      navigate(`/payment/success?order=${window.__ppOrderNo}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not confirm PayPal payment"); }
  };

  const canProceed = () => {
    if (step === 0) return form.full_name && form.phone && form.line1 && (user || form.email);
    return true;
  };

  const placeOrder = async () => {
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity, image: i.image, merchant_slug: i.merchant_slug, branch_id: i.branch_id, note: i.note, options: i.options })),
        address: { full_name: form.full_name, phone: form.phone, line1: form.line1, parish: form.parish, city: form.city, country: form.country, notes: form.notes, lat: form.lat, lng: form.lng, delivery_window: form.delivery_window || null },
        delivery_method: delivery, delivery_partner: deliveryPartner, payment_method: payment, age_confirmed: isAgeVerified(),
        email: user ? user.email : form.email, origin_url: window.location.origin, use_points: pointsVal,
        cart_session_id: cartSessionId(),
      };
      const { data } = await api.post("/checkout", payload);
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else if (data.payment_method === "bank_transfer") {
        clear();
        setBankInfo(data.bank_transfer);
      } else if (data.haulage_code) {
        clear();
        toast.success("Order placed — now compare driver offers");
        navigate(`/haulage/request/${data.haulage_code}`);
      } else {
        clear();
        toast.success("Order placed!");
        navigate(`/payment/success?order=${data.order_no}&cod=1`);
      }
    } catch (e) {
      toast.error("Checkout failed. Please try again.");
      setLoading(false);
    }
  };

  const uploadProof = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingProof(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/orders/${bankInfo.reference}/transfer-proof`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProofUrl(data.url);
      toast.success("Receipt uploaded — we'll confirm your payment shortly");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    setUploadingProof(false);
  };

  if (bankInfo) {
    const cur = bankInfo.currency;
    const amt = cur === "JMD" ? jmd(bankInfo.amount) : `${cur} ${Number(bankInfo.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    const rows = [
      ["Amount to transfer", amt],
      ["Bank", bankInfo.bank_name],
      ["Account number", `${cur} — ${bankInfo.account_number}`],
      ["Branch", bankInfo.branch],
      ["SWIFT code", bankInfo.swift],
      ["Transit / Branch", `${bankInfo.transit || "—"} / ${bankInfo.branch_code || "—"}`],
      ["Payment reference", bankInfo.reference],
    ];
    return (
      <div className="max-w-lg mx-auto px-4 py-12" data-testid="bank-transfer-instructions">
        <div className="bg-card rounded-2xl border border-border p-6 text-center">
          <div className="h-14 w-14 rounded-full bg-emerald/15 grid place-items-center mx-auto mb-3"><Landmark className="text-emerald" size={26} /></div>
          <h1 className="font-display text-2xl font-bold">Order placed — pay by bank transfer</h1>
          <p className="text-sm text-muted-foreground mt-1">Transfer the amount below to our National Commercial Bank account, quoting your reference. We ship as soon as payment is confirmed. A copy has been emailed to you.</p>
        </div>
        <div className="bg-card rounded-2xl border border-border mt-4 overflow-hidden">
          {rows.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between gap-3 px-5 py-3 border-b border-border last:border-0">
              <span className="text-sm text-muted-foreground">{k}</span>
              <span className="text-sm font-bold text-right flex items-center gap-2">{v}
                <button onClick={() => { navigator.clipboard?.writeText(String(v)); toast.success("Copied"); }} className="text-muted-foreground hover:text-ink" data-testid={`copy-${k.split(" ")[0].toLowerCase()}`}><Copy size={13} /></button>
              </span>
            </div>
          ))}
        </div>
        <div className="bg-card rounded-2xl border border-border mt-4 p-5" data-testid="transfer-proof-block">
          <div className="font-semibold text-sm mb-1">Already paid? Upload your receipt</div>
          <p className="text-xs text-muted-foreground mb-3">Send us your bank transfer / deposit slip (JPG, PNG or PDF) so we confirm and ship faster.</p>
          {proofUrl ? (
            <div className="flex items-center gap-2 text-sm text-emerald font-semibold" data-testid="proof-uploaded"><Check size={16} /> Receipt received — thank you!</div>
          ) : (
            <label className="inline-flex items-center gap-2 rounded-xl border border-border px-4 py-2.5 text-sm font-semibold cursor-pointer hover:bg-secondary" data-testid="proof-upload-label">
              {uploadingProof ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} {uploadingProof ? "Uploading…" : "Choose file"}
              <input type="file" accept="image/*,application/pdf" onChange={uploadProof} className="hidden" disabled={uploadingProof} data-testid="proof-upload-input" />
            </label>
          )}
        </div>
        <div className="flex gap-3 mt-4">
          <button onClick={() => navigate(`/track?order=${bankInfo.reference}`)} className="flex-1 bg-ink text-white rounded-xl py-3 font-semibold" data-testid="bank-track-btn">Track order</button>
          <button onClick={() => navigate("/")} className="flex-1 border border-border rounded-xl py-3 font-semibold" data-testid="bank-home-btn">Keep shopping</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="font-display text-3xl font-bold tracking-tight mb-6">Checkout</h1>
      <WeatherNotice parish={form.parish} country={form.country} chosenWindow={form.delivery_window} onChooseWindow={(w) => setForm((f) => ({ ...f, delivery_window: w }))} />

      <div className="flex items-center gap-2 mb-8 overflow-x-auto sn-scroll-x">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-2 shrink-0 ${i <= step ? "text-ink" : "text-muted-foreground"}`}>
              <span className={`h-8 w-8 grid place-items-center rounded-full text-sm font-bold ${i < step ? "bg-emerald text-white" : i === step ? "bg-ink text-white" : "bg-secondary"}`}>{i < step ? <Check size={16} /> : i + 1}</span>
              <span className="text-sm font-semibold hidden sm:block">{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 min-w-6 ${i < step ? "bg-emerald" : "bg-border"}`} />}
          </React.Fragment>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {step === 0 && (
            <div className="bg-card rounded-2xl border border-border p-6 space-y-4">
              <h3 className="font-display font-bold flex items-center gap-2"><MapPin size={18} className="text-emerald" /> Delivery Address</h3>
              {!user && <div className="text-xs bg-emerald/10 text-emerald-deep rounded-lg px-3 py-2">Checking out as guest — no account needed. <a href="/login" className="underline font-semibold">Sign in</a> to earn SHOP Points.</div>}
              <div className="grid sm:grid-cols-2 gap-4">
                <input value={form.full_name} onChange={set("full_name")} placeholder="Full name" className="rounded-xl border border-border px-4 py-2.5 text-sm" data-testid="checkout-name" />
                <input value={form.phone} onChange={set("phone")} placeholder="Phone number" className="rounded-xl border border-border px-4 py-2.5 text-sm" data-testid="checkout-phone" />
              </div>
              {!user && <input value={form.email} onChange={set("email")} placeholder="Email (for order updates)" className="w-full rounded-xl border border-border px-4 py-2.5 text-sm" data-testid="checkout-email" />}
              <input value={form.line1} onChange={set("line1")} placeholder="Street address" className="w-full rounded-xl border border-border px-4 py-2.5 text-sm" data-testid="checkout-address" />
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="relative">
                  <Globe size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald pointer-events-none" />
                  <select value={form.country} onChange={set("country")} className="w-full rounded-xl border border-border pl-9 pr-4 py-2.5 text-sm bg-background" data-testid="checkout-country">
                    {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                {intl ? (
                  <input value={form.parish} onChange={set("parish")} placeholder="State / Region / Province" className="rounded-xl border border-border px-4 py-2.5 text-sm" data-testid="checkout-region" />
                ) : (
                  <select value={form.parish} onChange={set("parish")} className="rounded-xl border border-border px-4 py-2.5 text-sm" data-testid="checkout-parish">
                    {parishes.map((p) => <option key={p}>{p}</option>)}
                  </select>
                )}
              </div>
              <input value={form.city} onChange={set("city")} placeholder="Town / City" className="w-full rounded-xl border border-border px-4 py-2.5 text-sm" data-testid="checkout-city" />
              {intl && <div className="text-xs bg-gold/10 text-ink rounded-lg px-3 py-2 flex items-center gap-2" data-testid="intl-notice"><Globe size={14} className="text-emerald" /> International delivery to <b>{form.country}</b> — a flat {jmd(6500)} worldwide shipping fee applies. From Kingston to the world. 🌍</div>}
              <input value={form.notes} onChange={set("notes")} placeholder="Delivery notes (optional)" className="w-full rounded-xl border border-border px-4 py-2.5 text-sm" />
            </div>
          )}

          {step === 1 && (
            <div className="bg-card rounded-2xl border border-border p-6 space-y-3">
              <h3 className="font-display font-bold flex items-center gap-2"><Truck size={18} className="text-emerald" /> How would you like your order delivered?</h3>
              {[
                { v: "pickup", t: "Merchant / Warehouse Pickup", s: "Collect from the merchant or SHOP NOW Kingston Warehouse", p: 0, label: "Free" },
                { v: "standard", t: "Standard SHOP NOW Delivery", s: "2–4 business days · nationwide & regional", p: 800 },
                { v: "express", t: "Express / Same-day", s: "Same/next day · Kingston Metro", p: 1500 },
                { v: "marketplace", t: "SHOP NOW Driver Marketplace", s: "Compare verified drivers' bids or book instantly — your order details carry over, no re-entry", p: 0, label: "Driver bids", badge: "New" },
                ...(intl ? [] : [{ v: "haulage", t: "Commercial Haulage", s: "Heavy or oversized items · trucks & flatbeds bid on your freight", p: 0, label: "Quote-first" }]),
                ...(intl ? [{ v: "international", t: "International Shipping", s: `Flat worldwide rate to ${form.country}`, p: 6500 }] : []),
              ].map((o) => (
                <button key={o.v} onClick={() => setDelivery(o.v === "international" ? "standard" : o.v)} className={`w-full flex items-center justify-between rounded-xl border-2 p-4 text-left transition-colors ${delivery === o.v || (o.v === "international" && delivery === "standard") ? "border-emerald bg-emerald/5" : "border-border"}`} data-testid={`delivery-${o.v}`}>
                  <div><div className="font-semibold flex items-center gap-2">{o.t}{o.badge && <span className="text-[10px] uppercase bg-emerald text-white rounded-full px-2 py-0.5">{o.badge}</span>}</div><div className="text-sm text-muted-foreground">{o.s}</div></div>
                  <div className="font-display font-bold text-right text-sm">{o.label || (o.p ? jmd(o.p) : "Free")}</div>
                </button>
              ))}
              {quoted && <div className="text-xs bg-gold/10 rounded-lg px-3 py-2" data-testid="delivery-quoted-note">Delivery is priced by the driver offer you accept after checkout — nothing is added here. {delivery === "haulage" ? "Heavy freight is matched to trucks with sufficient payload." : "Pickup, drop-off, weight and handling are passed to drivers automatically."}</div>}
              {quoted && <div className="space-y-2" data-testid="delivery-map-block"><HaulagePinMap pickup={{ parish: "Kingston" }} dropoff={{ parish: form.parish, lat: form.lat, lng: form.lng }} onDropoff={(p) => setForm((f) => ({ ...f, lat: p.lat, lng: p.lng }))} /></div>}
              {delivery !== "pickup" && !quoted && (
                <div className="pt-3 border-t border-border mt-1" data-testid="delivery-partner-block">
                  <div className="text-sm font-semibold mb-2 flex items-center gap-2">Preferred delivery partner <span className="text-[10px] uppercase bg-gold/20 text-ink rounded-full px-2 py-0.5">Pilot</span></div>
                  <div className="grid grid-cols-2 gap-2">
                    {["SHOP NOW Express", "7Crave", "Uber", "inDrive"].map((pp) => (
                      <button key={pp} type="button" onClick={() => setDeliveryPartner(pp)} className={`rounded-xl border-2 px-3 py-2.5 text-sm font-semibold text-left transition-colors ${deliveryPartner === pp ? "border-emerald bg-emerald/5" : "border-border"}`} data-testid={`partner-${pp.replace(/\s+/g, "-").toLowerCase()}`}>{pp}</button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">We'll coordinate your delivery hand-off with your chosen courier. Full in-app dispatch is coming with the SHOP NOW app.</p>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="bg-card rounded-2xl border border-border p-6 space-y-3">
              <h3 className="font-display font-bold flex items-center gap-2"><CreditCard size={18} className="text-emerald" /> Payment Method</h3>
              {[
                { v: "card", t: "Credit / Debit Card · Apple Pay · Google Pay", s: PAYMENT_METHODS_TEXT + " · secure Stripe checkout", icon: CreditCard, logos: true },
                ...(fygaroOn ? [{ v: "fygaro", t: "Fygaro (Jamaica)", s: "Pay by card in JMD via Fygaro's secure hosted checkout", icon: CreditCard }] : []),
                ...(paypalOn ? [{ v: "paypal", t: "PayPal", s: "Pay with PayPal or card (charged in USD)", icon: Wallet }] : []),
                { v: "bank_transfer", t: "Bank Transfer", s: "Direct deposit to our NCB account (JMD · USD · CAD · GBP)", icon: Landmark },
                { v: "cod", t: "Cash on Delivery", s: "Pay when your order arrives", icon: Banknote },
              ].map((o) => (
                <button key={o.v} onClick={() => setPayment(o.v)} className={`w-full flex items-center gap-3 rounded-xl border-2 p-4 text-left transition-colors ${payment === o.v ? "border-emerald bg-emerald/5" : "border-border"}`} data-testid={`payment-${o.v}`}>
                  <o.icon size={20} className="text-emerald" />
                  <div className="flex-1"><div className="font-semibold">{o.t}</div><div className="text-sm text-muted-foreground">{o.s}</div>{o.logos && <PaymentLogos size="sm" className="mt-2" />}</div>
                </button>
              ))}
              {user && summary?.points > 0 && (
                <div className="rounded-xl border border-border p-4">
                  <div className="flex items-center gap-2 font-semibold mb-2"><Wallet size={18} className="text-gold" /> SHOP Points</div>
                  <p className="text-sm text-muted-foreground mb-2">You have {summary.points.toLocaleString()} points (1 point = J$1, max J$10,000).</p>
                  <input type="range" min={0} max={Math.min(summary.points, 10000)} value={usePoints} onChange={(e) => setUsePoints(Number(e.target.value))} className="w-full accent-emerald" data-testid="points-slider" />
                  <div className="text-sm font-semibold text-emerald">Redeeming {jmd(pointsVal)}</div>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="bg-card rounded-2xl border border-border p-6 space-y-4">
              <h3 className="font-display font-bold">Review your order</h3>
              <div className="text-sm space-y-1">
                <div className="font-semibold">{form.full_name} · {form.phone}</div>
                <div className="text-muted-foreground">{form.line1}, {form.city} {form.parish}</div>
                <div className="text-muted-foreground capitalize">{delivery} delivery · {payment === "card" ? "Card" : "Cash on delivery"}</div>
              </div>
              <div className="divide-y divide-border">
                {items.map((i) => (
                  <div key={i.product_id} className="flex items-center gap-3 py-3">
                    <img src={i.image} alt="" className="h-14 w-14 rounded-lg object-cover" />
                    <div className="flex-1 text-sm"><div className="font-medium line-clamp-1">{i.name}</div><div className="text-muted-foreground">Qty {i.quantity}</div></div>
                    <div className="font-semibold text-sm">{jmd(i.price * i.quantity)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-between">
            {step > 0 ? <button onClick={() => setStep((s) => s - 1)} className="px-5 py-2.5 rounded-xl border border-border font-semibold">Back</button> : <span />}
            {step < 3 ? (
              <button onClick={() => canProceed() ? setStep((s) => s + 1) : toast.error("Please fill in all required fields")} className="px-6 py-2.5 rounded-xl bg-ink text-white font-semibold" data-testid="checkout-next">Continue</button>
            ) : payment === "paypal" && paypalOn && paypalClientId ? (
              <div className="w-full max-w-xs" data-testid="paypal-buttons">
                <PayPalScriptProvider options={{ "client-id": paypalClientId, currency: "USD" }}>
                  <PayPalButtons style={{ layout: "vertical", color: "gold", shape: "pill" }} createOrder={paypalCreate} onApprove={paypalApprove} />
                </PayPalScriptProvider>
              </div>
            ) : (
              <button onClick={placeOrder} disabled={loading} className="px-6 py-2.5 rounded-xl bg-emerald text-white font-semibold flex items-center gap-2 disabled:opacity-60" data-testid="checkout-submit-btn">
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />} {payment === "cod" || payment === "bank_transfer" ? "Place Order" : "Pay Now"}
              </button>
            )}
          </div>
        </div>

        <div className="bg-card rounded-2xl border border-border p-6 h-fit sticky top-28">
          <h3 className="font-display font-bold text-lg mb-4">Summary</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-semibold">{jmd(subtotal)}</span></div>
            {discountPct > 0 && <div className="flex justify-between text-emerald"><span>Discount ({discountPct}%)</span><span className="font-semibold">−{jmd(discountAmt)}</span></div>}
            <div className="flex justify-between"><span className="text-muted-foreground">Delivery</span><span className="font-semibold">{quoted ? "Driver bid" : fee ? jmd(fee) : "Free"}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Includes GCT (15%)</span><span className="font-semibold">{jmd(tax)}</span></div>
            {pointsVal > 0 && <div className="flex justify-between text-emerald"><span>SHOP Points</span><span className="font-semibold">−{jmd(pointsVal)}</span></div>}
          </div>
          <div className="border-t border-border mt-4 pt-4 flex justify-between font-display font-bold text-lg"><span>Total</span><span>{jmd(total)}</span></div>
          {payment === "card" && <p className="text-xs text-muted-foreground mt-2">Charged in USD equivalent via secure Stripe checkout.</p>}
        </div>
      </div>
    </div>
  );
}
